// Shared RBAC vocabulary — the client half of the backend's internal/authz package.
// Module keys and levels MUST stay in sync with authz.go (they are the map keys of the
// permission_groups documents, the login/users-me `permissions` payload, and the rows of
// the Settings → Team → Access groups matrix). Never rename a key without a data migration.

export const PERMISSION_LEVELS = Object.freeze(["none", "view", "manage"]);

export const LEVEL_RANK = Object.freeze({ none: 0, view: 1, manage: 2 });

// Ordered rows of the function matrix. `label`/`description` drive the matrix editor and
// any permission-related hints; `key` is the wire identifier.
export const PERMISSION_MODULES = Object.freeze([
  {
    key: "messages",
    label: "Patient messages",
    description: "WhatsApp threads: read the list vs. reply, lock, tag and manage threads",
  },
  {
    key: "chats",
    label: "Internal chats",
    description: "Staff-to-staff chats (also requires an eligible user type)",
  },
  {
    key: "ai_drafts",
    label: "AI drafting",
    description: "Rewrite-with-AI and quick rewrite in the patient composer (sending still needs Patient messages → Manage)",
  },
  {
    key: "tasks",
    label: "Tasks",
    description: "Team task queue: see tasks vs. create, claim, snooze and complete",
  },
  {
    key: "calendar",
    label: "Calendar",
    description: "Appointments: view schedules vs. book, move, edit and cancel",
  },
  {
    key: "scheduling",
    label: "Scheduling setup",
    description: "Slot definitions, time off / holidays, session rosters and work-plan postings",
  },
  {
    key: "work_plan",
    label: "Work plan",
    description: "The day planner and MC queue: read the day vs. publish it, copy a day in and decide medical certificates",
  },
  {
    key: "queue",
    label: "Patient queue",
    description: "Live day board: view rosters vs. check-in, move and run visits",
  },
  {
    key: "queue_config",
    label: "Queue configuration",
    description: "Queue definitions in Settings (swimlanes, entry types, events)",
  },
  {
    key: "patients",
    label: "Patients",
    description: "Patient directory: browse profiles vs. create, edit and merge",
  },
  {
    key: "staff",
    label: "Team & access",
    description: "Employee accounts and access groups (admin-level changes stay admin-only)",
  },
  {
    key: "settings",
    label: "Global settings",
    description: "Tenant-wide shared settings: thread groups, bot-ack window, teams",
  },
  {
    key: "integrations",
    label: "Integrations",
    description: "WhatsApp gateway connections: see link status vs. show the QR and re-bind a number",
  },
  {
    key: "knowledge",
    label: "Knowledge",
    description: "Knowledge Base and Trigger Base content",
  },
  {
    key: "archive",
    label: "Archive search",
    description: "Historical archived messages: search and read them vs. also download archived media",
  },
  {
    key: "dashboard",
    label: "Dashboard",
    description: "Landing-page announcements: every staff member reads the feed; Manage lets you post, edit and delete",
  },
]);

export const PERMISSION_MODULE_KEYS = Object.freeze(PERMISSION_MODULES.map((m) => m.key));

const MODULE_KEY_SET = new Set(PERMISSION_MODULE_KEYS);

export function isValidPermissionModule(key) {
  return MODULE_KEY_SET.has(key);
}

export function normalizePermissionLevel(value) {
  const s = typeof value === "string" ? value.trim().toLowerCase() : "";
  return s === "view" || s === "manage" ? s : "none";
}

// normalizePermissionsMap coerces a raw {module: level} payload into a clean map holding
// only known modules at view/manage (none is expressed by absence — matches the backend's
// ToWire shape). Unknown modules are dropped: fail closed.
export function normalizePermissionsMap(raw) {
  const out = {};
  if (!raw || typeof raw !== "object") {
    return out;
  }
  for (const [module, level] of Object.entries(raw)) {
    if (!MODULE_KEY_SET.has(module)) continue;
    const normalized = normalizePermissionLevel(level);
    if (normalized !== "none") {
      out[module] = normalized;
    }
  }
  return out;
}

// levelAtLeast: does `level` satisfy the `min` requirement (none < view < manage)?
export function levelAtLeast(level, min) {
  return (LEVEL_RANK[normalizePermissionLevel(level)] ?? 0) >= (LEVEL_RANK[min] ?? 0);
}

// mergePermissionMaps unions several {module: level} maps taking the per-module MAX — a
// person's effective rights across all their permission groups. Mirrors internal/authz.Union
// in healo_backend: unknown modules dropped, none expressed by absence.
export function mergePermissionMaps(maps = []) {
  const out = {};
  for (const raw of maps) {
    for (const [module, level] of Object.entries(normalizePermissionsMap(raw))) {
      if ((LEVEL_RANK[level] ?? 0) > (LEVEL_RANK[out[module]] ?? 0)) {
        out[module] = level;
      }
    }
  }
  return out;
}

// Hideable items — a SEPARATE namespace from the module keys above: modules gate FUNCTIONS
// (server-enforced), menu keys trim VISIBILITY only, so items sharing a module (Slots + Sessions
// + Work Plan + Time Off → scheduling/work_plan; Knowledge Base + Trigger Base → knowledge) can
// be hidden individually. Mirrors internal/authz menu constants in healo_backend.
//
// The vocabulary spans two surfaces, distinguished by `group`: the nav sidebar and the Settings
// rail (the settings_* keys, one per sub-page). Never hideable, and therefore absent here:
// Dashboard (the login landing page) and the Settings NAV ITEM itself — there is no bare
// "settings" key. `getVisibleSettingsSubPages` additionally keeps a runtime floor so a group
// hiding every settings_* key still lands on Preferences rather than an empty Settings shell.
//
// `work_plan` deliberately exists in BOTH namespaces (as do messages/chats/patients/tasks/
// calendar) — the two Sets are independent; do not "de-duplicate" them.
//
// Hides union across a person's groups (hidden in any group = hidden); admins see everything.
export const MENU_ITEMS = Object.freeze([
  { key: "messages", label: "Messages", group: "Main menu" },
  { key: "chats", label: "Chats", group: "Main menu" },
  { key: "patients", label: "Patients", group: "Main menu" },
  { key: "tasks", label: "Tasks", group: "Main menu" },
  { key: "calendar", label: "Calendar", group: "Main menu" },
  { key: "slots", label: "Slots", group: "Main menu" },
  { key: "sessions", label: "Sessions", group: "Main menu" },
  { key: "work_plan", label: "Work Plan", group: "Main menu" },
  { key: "time_off", label: "Time Off", group: "Main menu" },
  { key: "search", label: "Search", group: "Main menu" },
  { key: "knowledge_base", label: "Knowledge Base", group: "Main menu" },
  { key: "trigger_base", label: "Trigger Base", group: "Main menu" },
  { key: "settings_preferences", label: "Preferences", group: "Settings" },
  { key: "settings_thread_groups", label: "Thread groups", group: "Settings" },
  { key: "settings_appointment_confirmation", label: "Appointment confirmations", group: "Settings" },
  { key: "settings_message_templates", label: "Message templates", group: "Settings" },
  { key: "settings_clinic_visibility", label: "Clinic visibility", group: "Settings" },
  { key: "settings_face_unlock", label: "Face unlock", group: "Settings" },
  { key: "settings_work_roles", label: "Work roles", group: "Settings" },
  { key: "settings_work_plan", label: "Work plan", group: "Settings" },
  { key: "settings_calendars", label: "Calendars", group: "Settings" },
  { key: "settings_queues", label: "Queues", group: "Settings" },
  { key: "settings_bot_service_hours", label: "Bot working hours", group: "Settings" },
  { key: "settings_whatsapp_connections", label: "WhatsApp Connections", group: "Settings" },
  { key: "settings_employees", label: "Employees", group: "Settings" },
  { key: "settings_access_groups", label: "Access groups", group: "Settings" },
]);

// MENU_GROUPS — MENU_ITEMS collapsed into consecutive {group, items} runs so the editor can
// render subheaders. ORDER-SENSITIVE: a Settings item inserted mid-way through the Main menu run
// yields a duplicated header rather than an error. Grouping is render-only — the FLAT MENU_ITEMS
// order stays the canonical wire order (see normalizeHiddenMenuItems), so never sort through
// this; the two orders would drift.
export const MENU_GROUPS = Object.freeze(
  MENU_ITEMS.reduce((acc, item) => {
    const last = acc[acc.length - 1];
    if (last && last.group === item.group) last.items.push(item);
    else acc.push({ group: item.group, items: [item] });
    return acc;
  }, []),
);

const MENU_KEY_SET = new Set(MENU_ITEMS.map((m) => m.key));

// normalizeHiddenMenuItems coerces a raw hidden-item payload into a clean, de-duplicated
// array of known keys in canonical order (unknown keys dropped — fail closed).
export function normalizeHiddenMenuItems(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  for (const key of raw) {
    const k = typeof key === "string" ? key.trim().toLowerCase() : "";
    if (MENU_KEY_SET.has(k)) seen.add(k);
  }
  return MENU_ITEMS.filter((m) => seen.has(m.key)).map((m) => m.key);
}

// Hidden Messages clinic tabs — WhatsApp gateway botIds a group hides from the clinic-tab row
// (the "All / Camden / …" control). Unlike MENU_ITEMS there is NO fixed vocabulary: the ids are
// per-tenant WhatsApp config, so this cleans (trim / drop blanks / de-dup / sort) rather than
// validating against a known list. Mirrors backend authz.ValidateHiddenClinics. UI trimming
// only — hidden tabs still appear under "All"; admins see every tab.
export function normalizeHiddenClinics(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  for (const key of raw) {
    const k = typeof key === "string" ? key.trim() : "";
    if (k) seen.add(k);
  }
  return Array.from(seen).sort();
}
